
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
    import EingetragenAmVom from '@/components/EingetragenAmVom.vue';
    import Karteieintrag from '@/types/Karteieintrag';
    import moment from 'moment';
    import { SweetAlertResult } from 'sweetalert2';
    import { confirmSwal } from '@/helper/swals';
    import Patient from '@/types/Patient';
    import PatUntersuchung from '@/types/PatUntersuchung';
    import Untersuchung from '@/types/Untersuchung';
    import Text from '@/types/Text';
    import { Verrechnungsart } from '@/types/Verrechnungsart';
    import { ValidationRules } from '@/store/types';
    import { createAutocompleteFilter, VCurrencyField, VForm } from '@/types/Components';
    import { LoadTextePayload } from '@/store/texte';
    import { LoadPersonenPayload } from '@/store/personen';
    import { UpdateUntersuchungPayload } from '@/store/patient';
    import { SystemEinstellung, SystemEinstellungKey } from '@/types/Einstellung';
    import { Patgruppe } from '@/types/Patgruppe';
    import { SelectItems } from '@/types/SelectItems';
    import Impfung from '@/types/Impfung';

    @Component({
        components: {
            EingetragenAmVom,
        },
        computed: {
            ...mapState('globals', ['rules', 'formatDate', 'parseDate', 'formatTime', 'parseTime']),
            ...mapState('session', ['arzt', 'verrechnungUntersuchung']),
            ...mapState('patient', ['patientData', 'loaders']),
            ...mapState('patient', { patUntersuchungen: 'untersuchungen' }),
            ...mapState('authentication', ['impfstelle_id']),
            ...mapState('untersuchungen', ['untersuchungen', 'platzhalter']),
            ...mapState('untersuchungen', { untersuchungenLoaders: 'loaders' }),
            ...mapGetters('personen', ['getPersonen']),
            ...mapState('personen', { personenLoaders: 'loaders' }),
            ...mapState('texte', ['texte']),
            ...mapState('texte', { texteLoaders: 'loaders' }),
            ...mapGetters('authentication', ['getSystemEinstellung']),
        },
        methods: {
            ...mapMutations('session', ['setSelectedArzt', 'setSelectedVerrechnungUntersuchung']),
            ...mapActions('patient', [
                'updateUntersuchung',
                'printUntersuchung',
                'printUntersuchungMitBefund',
                'deleteUntersuchung',
                'loadImpfungen',
                'getReiseplaene',
                'setSelectedOffenNext',
                'updateUntersuchungAbrechnung',
            ]),
            ...mapActions('patient', { loadPatUntersuchungen: 'loadUntersuchungen' }),
            ...mapActions('untersuchungen', ['loadUntersuchungen']),
            ...mapActions('personen', ['loadPersonen']),
            ...mapActions('texte', ['loadTexte']),
        },
    })
    export default class UntersuchungDetail extends Vue {
        @Prop() visible!: boolean;
        @Prop() value!: Karteieintrag;

        public $refs!: {
            form: VForm;
            befundwert: VCurrencyField;
        };

        public search = '';

        public rules!: ValidationRules;
        public patientData!: Patient | null;
        public untersuchungen!: Untersuchung[];
        public arzt!: number | null;
        public impfstelle_id!: number;
        public verrechnungUntersuchung!: string | null;
        public patUntersuchungen!: PatUntersuchung[];
        public parseDate!: (input: string | null) => string | null;
        public formatDate!: (input: string | null) => string | null;
        public parseTime!: (input: string | null) => string | null;
        public formatTime!: (input: string | null) => string | null;
        public loadImpfungen!: () => void;
        public getReiseplaene!: () => void;
        public loadPersonen!: (payload?: LoadPersonenPayload) => void;
        public loadTexte!: (payload?: LoadTextePayload) => void;
        public loadUntersuchungen!: () => void;
        public updateUntersuchung!: (payload: UpdateUntersuchungPayload) => void;
        public deleteUntersuchung!: () => void;
        public setSelectedOffenNext!: () => void;
        public loadPatUntersuchungen!: () => void;
        public getSystemEinstellung!: <T>(parameter: SystemEinstellungKey) => SystemEinstellung<T>;
        public setSelectedVerrechnungUntersuchung!: (payload: any) => void;
        public updateUntersuchungAbrechnung!: (payload?: {patimpfung_id: number | null, abrechnung: number| null, hcw: string| null}) => void;

        public untersuchung = new PatUntersuchung();
        public reloadImpfungen = false;
        public text = new Text();
        public befundwerttexte: string[] = [];
        public disabledWert = false;
        public beleg: { jahr: number | null, belegnr: number | null } | null = null;
        public isOffen = false;
        public isBefundwertNumerisch = false;
        public defaultBefundwert: string | number = 0;
        public befundVergleichsoperatoren = SelectItems.BEFUND_VERGLEICHSOPERATOREN;

        public readonly untersuchungenFilter = createAutocompleteFilter<Untersuchung>('untersuchung', 'code');

        @Watch('untersuchungen')
        public onUntersuchungenLoaded(newValue: Untersuchung[]): void {
            const todaysUntersuchungen = this.patUntersuchungen.filter(
                (patUntersuchung) => moment(patUntersuchung.abnahmedatum, 'YYYY-MM-DD HH:mm:ss').isSame(moment(), 'day'));

            this.untersuchung.untersuchungen = newValue
                .filter((
                    { untersuchung_id, select_by_default }) => untersuchung_id != null && 
                        select_by_default === 'j' && 
                        !todaysUntersuchungen.some((todaysUntersuchung) => todaysUntersuchung.untersuchung_id === untersuchung_id))
                .map(({ untersuchung_id }) => untersuchung_id as number);
        }

        public get isNew(): boolean {
            return this.untersuchung.patuntersuchung_id === null || this.untersuchung.patuntersuchung_id === undefined;
        }

        public get maxZeitraum(): string {
            return moment().format('YYYY-MM-DD');
        }

        public get selectedUntersuchung(): Untersuchung | null {
            if (this.untersuchung.untersuchung_id) {
                return this.untersuchungen.find((untersuchung) => {
                    if (this.isNew || !this.untersuchung.person_arzt) {
                        this.untersuchung.verrechnung = untersuchung.verrechnung;
                    }
                    return untersuchung.untersuchung_id === this.untersuchung.untersuchung_id;
                }) ?? null;
            } else {
                return this.untersuchungen.find((untersuchung) => {
                    if (this.untersuchung.untersuchungen[0] && this.untersuchung.untersuchungen.length === 1) {
                        this.untersuchung.verrechnung = untersuchung.verrechnung;
                    }
                    return untersuchung.untersuchung_id === this.untersuchung.untersuchungen[0];
                }) ?? null;
            }
        }

        public get einheit(): string | null {
            if (!this.selectedUntersuchung) {
                return this.value?.untersuchung?.untersuchung_einheit ?? null;
            }
            return this.selectedUntersuchung?.einheit ?? null;
        }

        @Watch('visible')
        public onChangeVisible(visible: boolean): void {
            if (!visible) {
                if (this.reloadImpfungen) {
                    this.loadImpfungen();
                    this.getReiseplaene();
                    this.loadPatUntersuchungen();
                }

                setTimeout(() => this.setSelectedOffenNext(), 100);

                return;
            }

            this.reloadImpfungen = false;
            this.text = new Text();

            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }

            this.untersuchung = new PatUntersuchung(this.value.untersuchung);
            this.untersuchung.hcw = this.value.hcw;
            this.isOffen = this.untersuchung.isGeplant;

            this.beleg = this.value.abrechnung === Verrechnungsart.Bezahlt
                ? {
                    jahr: this.value.jahr ?? this.value.beleg_jahr,
                    belegnr: this.value.belegnr ?? this.value.beleg_belegnr
                }
                : null;

            if (this.isNew || this.isOffen) {
                this.untersuchung.abnahmedatum = moment().format('YYYY-MM-DD');
                this.abnahmezeit = '';
                this.untersuchung.patnr = this.patientData?.patnr ?? null;
                //this.untersuchung.hcw = this.value.hcw;
                //this.untersuchung.verrechnung = 'n';
                this.untersuchung.vergleichsoperator = '2';

                if (this.arzt) {
                    this.untersuchung.person_id_arzt = this.arzt;
                }
            } else {
                this.abnahmezeit = moment(this.untersuchung.abnahmedatum).format('HH:mm');

                if (this.value.abrechnung === Verrechnungsart.Offen) {
                    this.untersuchung.verrechnung = 'j';
                } else {
                    this.untersuchung.verrechnung = 'n';
                }

                this.untersuchung.hcw = this.value.hcw;
            }

            if (!this.untersuchung.person_id_arzt && this.untersuchung.patplan_id) {
                if (this.untersuchung.untersuchung_verrechnung === 'j') {
                    this.untersuchung.verrechnung = 'j';
                } else {
                    this.untersuchung.verrechnung = 'n';
                }
            }

            this.loadUntersuchungen();
            this.loadPersonen();

            if (this.untersuchung.befunddatum) {
                this.untersuchung.befunddatum = moment(this.untersuchung.befunddatum).format('YYYY-MM-DD');
            }

            if (this.untersuchung.abnahmedatum) {
                this.untersuchung.abnahmedatum = moment(this.untersuchung.abnahmedatum).format('YYYY-MM-DD');
            }

            this.loadTexte();

            this.isBefundwertNumerisch = this.getSystemEinstellung('befundwert_numerisch').isEnabled() &&
                typeof this.untersuchung.befundwert !== 'string';
            this.defaultBefundwert = this.untersuchung.befundwert ?? (this.isBefundwertNumerisch ? '' : '');

            this.disabledWert = false;
            this.befundwerttexte = [];

            this.onChangeBefundwerttext(this.untersuchung.befundwerttext);

            if (this.untersuchung.untersuchung_id) {
                this.createBefundwertTexte();
            }

            this.abnahmedatumErrorMessages = [];
            this.befunddatumErrorMessages = [];
            this.abnahmedatumFormatted = this.formatDate(this.untersuchung.abnahmedatum);
            this.befunddatumFormatted = this.formatDate(this.untersuchung.befunddatum);

            /*            if (this.isBefundwertNumerisch) {
                this.$nextTick(() => this.$refs.befundwert?.setValue(this.defaultBefundwert as number));
            }*/
        }

        public createBefundwertTexte(): void {
            if (this.untersuchung.untersuchungen.length !== 1 && !this.untersuchung.untersuchung_id) {
                return;
            }

            const variante = this.selectedUntersuchung?.untersuchungsbefundvariante ??
                this.untersuchung.untersuchung_untersuchungsbefundvariante;

            switch (Number.isInteger(variante) ? variante : parseInt(variante as string, 10)) {
                case 1:
                    this.befundwerttexte = ['neg.', 'pos.'];
                    break;
                case 2:
                    this.befundwerttexte = ['neg.', 'pos.', 'grenzw.'];
                    break;
                case 3:
                    this.befundwerttexte = ['neg.', 'Wert'];
                    break;
                case 4:
                    this.befundwerttexte = ['neg.', '1:'];
                    break;
                case 5:
                    this.befundwerttexte = ['neg.'];
                    break;
                case 6:
                    this.befundwerttexte = [
                        '0 pos.',
                        '0 neg.',
                        'A pos.',
                        'A neg.',
                        'B pos.',
                        'B neg.',
                        'AB pos.',
                        'AB neg.',
                        'pos./neg.?',
                    ];
                    break;
                case 7:
                    this.befundwerttexte = ['neg.', 'pos.', 'grenzw.', 'Wert', '1:'];
                    break;
                case 8:
                    this.befundwerttexte = ['neg.', 'pos.', 'schwach pos.', 'grenzw.'];
                    break;
                case 9:
                    this.befundwerttexte = ['niedrig avid', 'hoch avid'];
                    break;
                default:
                    this.befundwerttexte = [];
                    break;
            }

            if (this.isNew) {
                if (this.befundwerttexte.length === 1) {
                    const [befundwerttext] = this.befundwerttexte;
                    this.untersuchung.befundwerttext = befundwerttext;
                } else {
                    this.untersuchung.befundwerttext = null;
                }
            }
        }

        @Watch('loaders.untersuchung.save')
        public onChangeSave(saving: boolean): void {
            if (!saving && this.visible && !this.isNew) {
                this.untersuchung = new PatUntersuchung(this.value.untersuchung);
                this.abnahmezeit = moment(this.untersuchung.abnahmedatum).format('HH:mm');

                if (this.untersuchung.befunddatum) {
                    this.untersuchung.befunddatum = moment(this.untersuchung.befunddatum).format('YYYY-MM-DD');
                }

                if (this.untersuchung.abnahmedatum) {
                    this.untersuchung.abnahmedatum = moment(this.untersuchung.abnahmedatum).format('YYYY-MM-DD');
                }

                this.abnahmedatumErrorMessages = [];
                this.abnahmezeitErrorMessages = [];
                this.befunddatumErrorMessages = [];
                this.abnahmedatumFormatted = this.formatDate(this.untersuchung.abnahmedatum);
                this.abnahmezeitFormatted = this.formatTime(this.abnahmezeit);
                this.befunddatumFormatted = this.formatDate(this.untersuchung.befunddatum);
            }
        }

        @Watch('untersuchung.befundwerttext')
        public onChangeBefundwerttext(befundwerttext: string | null): void {
            this.disabledWert = !['grenzw.', 'Wert', '1:'].includes(befundwerttext ?? '');

            if (this.disabledWert) {
                //this.untersuchung.befundwert = null;
            }
        }

        @Watch('text')
        public onChangeText({ text_id, text }: Text): void {
            if (text_id) {
                this.untersuchung.interpretation = text;
            }
        }

        /*@Watch('untersuchung.vergleichsoperator')
        public onChangeVergleichsoperator(): void {
            if (this.untersuchung.vergleichsoperator?.includes('<')) {
                this.untersuchung.vergleichsoperator = '<';
            }
        }*/

        public onClickSave(): void {
            if (!this.$refs.form.validate() ||
                this.abnahmedatumErrorMessages.length > 0 ||
                this.befunddatumErrorMessages.length > 0 ||
                this.abnahmezeitErrorMessages.length > 0) {
                return;
            }

            if (this.isHCWEnabled && this.untersuchung.hcw === 'n' && (this.isNew || this.untersuchung.patplan_id) && this.getSystemEinstellung('muw').isEnabled() &&
                (this.patientData?.patientengruppe_id === Patgruppe.HCW_MUW || this.patientData?.patientengruppe_id === Patgruppe.HCW_STUDENT)) {
                confirmSwal(
                    '',
                    'Ist Untersuchung HCW?',
                    'Ja',
                    'Nein')
                    .then(({ isConfirmed }: SweetAlertResult) => {
                        if (isConfirmed) {
                            this.untersuchung.hcw = 'j';
                        }
                        this.updateUntersuchung({
                            data: {
                                impfstelle_id: this.value.impfstelle_id ?? this.impfstelle_id,
                                patimpfung_id: this.value.patimpfung_id,
                                untersuchung: new PatUntersuchung({
                                    ...this.untersuchung,
                                    abnahmedatum: `${this.untersuchung.abnahmedatum} ${this.abnahmezeit}`,
                                }),
                            },
                            closeDialog: this.isNew || this.isOffen,
                            anforderungsdruck: this.untersuchung.untersuchungen.length > 0
                        });

                        this.reloadImpfungen = !this.isNew;
                    });
            } else {
                this.updateUntersuchung({
                    data: {
                        impfstelle_id: this.value.impfstelle_id ?? this.impfstelle_id,
                        patimpfung_id: this.value.patimpfung_id,
                        untersuchung: new PatUntersuchung({
                            ...this.untersuchung,
                            abnahmedatum: `${this.untersuchung.abnahmedatum} ${this.abnahmezeit}`,
                        }),
                    },
                    closeDialog: this.isNew || this.isOffen,
                    anforderungsdruck: this.untersuchung.untersuchungen.length > 0
                });

                this.reloadImpfungen = !this.isNew;
            }
        }

        public onClickDelete(): void {
            confirmSwal('Löschen', 'Untersuchung endgültig löschen?', 'Löschen', 'Abbrechen')
                .then(({ isConfirmed }: SweetAlertResult) => {
                    if (isConfirmed) {
                        this.deleteUntersuchung();
                    }
                });
        }

        public abnahmedatumMenu = false;
        public abnahmedatumFormatted: string | null = null;
        public abnahmedatumErrorMessages: string[] = [];

        @Watch('untersuchung.abnahmedatum')
        public onChangeAbnahmedatum(date: string | null): void {
            this.abnahmedatumFormatted = this.formatDate(this.untersuchung.abnahmedatum);

            if (date !== null && this.isNew) {
                this.abnahmedatumErrorMessages = [];

                for (const rule of [this.rules.required, this.rules.validDate]) {
                    const valid = rule(this.untersuchung.abnahmedatum);
                    if (valid !== true) {
                        this.abnahmedatumErrorMessages.push(valid as string);
                    }
                }

                if (this.untersuchung.abnahmedatum && (this.untersuchung.abnahmedatum > moment().format('YYYY-MM-DD'))) {
                    this.abnahmedatumErrorMessages.push('Abnahmedatum darf nicht in der Zukunft sein.');
                }
            }
        }

        public abnahmezeit: string | null = '';
        public abnahmezeitMenu = false;
        public abnahmezeitFormatted: string | null = null;
        public abnahmezeitErrorMessages: string[] = [];

        @Watch('abnahmezeit')
        public onChangeAbnahmezeit(time: string | null): void {
            this.abnahmezeitFormatted = this.formatTime(time);

            if (time && this.isNew) {
                this.abnahmezeitErrorMessages = [];

                for (const rule of [this.rules.validTime]) {
                    const valid = rule(time);
                    if (valid !== true) {
                        this.abnahmezeitErrorMessages.push(valid as string);
                    }
                }
            }
        }

        public befunddatumMenu = false;
        public befunddatumFormatted: string | null = null;
        public befunddatumErrorMessages: string[] = [];

        @Watch('untersuchung.befunddatum')
        public onChangeBefunddatum(date: string | null): void {
            this.befunddatumFormatted = this.formatDate(this.untersuchung.befunddatum);

            if (date !== null && date !== '') {
                this.befunddatumErrorMessages = [];

                for (const rule of [this.rules.validDate]) {
                    const valid = rule(this.untersuchung.befunddatum);
                    if (valid !== true) {
                        this.befunddatumErrorMessages.push(valid as string);
                    }
                }

                if (this.untersuchung.befunddatum && (this.untersuchung.befunddatum > moment().format('YYYY-MM-DD'))) {
                    this.befunddatumErrorMessages.push('Befunddatum darf nicht in der Zukunft sein.');
                }
            }
        }

        get isHCWEnabled(): boolean {
            if (this.value.hcw) {
                this.untersuchung.hcw = this.value.hcw;

                var untersuchungValue = this.untersuchungen.find((untersuchung: Untersuchung) => untersuchung.untersuchung_id === this.value.untersuchung?.untersuchung_id);

                if (untersuchungValue?.hcw === '1' || untersuchungValue?.hcw === '2') {
                    return true;
                } return false;
            } else if (this.untersuchung.untersuchung_id || !this.werteDisabled) {
                var untersuchungID = 0;

                if (this.untersuchung.untersuchung_id) {
                    untersuchungID = this.untersuchung.untersuchung_id;
                }

                if (this.untersuchung.untersuchungen.length === 1) {
                    untersuchungID = this.untersuchung.untersuchungen[0];
                }

                var untersuchung = this.untersuchungen.find((untersuchung: Untersuchung) => untersuchung.untersuchung_id === untersuchungID);

                if (untersuchung?.hcw === '1') {
                    if (this.isNew || this.isOffen) {
                        this.untersuchung.hcw = 'j';
                    }
                    return true;
                } else if (untersuchung?.hcw === '2') {
                    if (this.isNew || this.isOffen) {
                        this.untersuchung.hcw = 'n';
                    }
                    return true;
                } else if (untersuchung?.hcw === '0') {
                    this.untersuchung.hcw = 'n';
                    return false;
                } else {
                    this.untersuchung.hcw = null;
                }
            }

            return false;
        }

        public onChangeAbrechnung(): void {
            this.setSelectedVerrechnungUntersuchung(this.untersuchung.verrechnung ?? 'n');

            /*if (!this.isNew) {
                this.updateUntersuchungAbrechnung({
                    patimpfung_id: this.value.patimpfung_id,
                    abrechnung: this.untersuchung.verrechnung === 'j' ? Verrechnungsart.Offen : Verrechnungsart.NichtVerrechenbar,
                    hcw: this.untersuchung.hcw
                });
                //this.reloadImpfungen = true;
            }*/
        }

        public get werteDisabled() {
            if (this.untersuchung.untersuchung_id) {
                return false;
            } else if (this.untersuchung.untersuchungen.length === 1) {
                return false;
            } else if (this.untersuchung.untersuchungen.length > 1) {
                this.untersuchung.hcw = 'n';
                this.untersuchung.verrechnung = 'n';
                return true;
            } else {
                this.untersuchung.hcw = 'n';
                this.untersuchung.verrechnung = 'n';
                return true;
            }
        }

        public get disabledVergleichsoprator() {
            if (this.werteDisabled) {
                return true;
            } else {
                return false;
            }
        }
    }
